import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';
import { getJobStartAtDialogOpen } from 'lib/selectors/getJobStartAtDialog';
import BaseJobTimeDialog from 'components/BaseJobTimeDialog';
import { TickerContext } from 'components/TickerProvider';
import _get from 'lodash/get';
import { toISO, safeConvertToISO } from 'lib/utils/date';
import { getCompany } from 'lib/selectors/getCompany';
import { getMachine } from 'lib/selectors/getMachine';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { getLatestOperatorRun } from 'lib/selectors/getLatestOperatorRun';
import { actionCloseJobStartAtDialog } from 'lib/actions';
import useTimeOptions from 'lib/hooks/useTimeOptions';
import {
  TIME_OPTION_FLOW,
  buildSkeletonOptions,
  buildFailureOptions,
  mapApiOptionsToUiOptions,
  resolveJobStartThirdKey,
} from 'lib/utils/timeOptions';
import JobStartAtDialogEventEmitter from './emitter';

const JobStartAtDialogBase = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nowMs = useContext(TickerContext);

  const company = useSelector(getCompany);
  const machine = useSelector(getMachine);
  const latestActivity = useSelector(getLatestActivity);
  const latestOperatorRun = useSelector(getLatestOperatorRun);
  const machineRef = machine?.machineRef;
  const tzid = _get(company, 'timezone');

  const openedAtRef = useRef(null);
  if (!openedAtRef.current) {
    openedAtRef.current = toISO(nowMs);
  }

  // Resolved once at mount (same pattern as openedAtRef) so the skeleton's
  // third row shows the real label the API will pick.
  const thirdKeyRef = useRef(null);
  if (!thirdKeyRef.current) {
    thirdKeyRef.current = resolveJobStartThirdKey({
      lastJobEndAt: safeConvertToISO(latestActivity?.end, tzid),
      signInAt: safeConvertToISO(latestOperatorRun?.startAt, tzid),
    });
  }

  const nowISO = toISO(nowMs);

  const jobStartBody = useMemo(() => {
    return {
      jobStart: { openedAt: openedAtRef.current },
    };
  }, []);

  const { error, options } = useTimeOptions({
    enabled: !!machineRef,
    machineRef,
    flow: TIME_OPTION_FLOW.JOB_START,
    body: jobStartBody,
  });

  const optionsList = useMemo(() => {
    const buildOpts = { jobStartThirdKey: thirdKeyRef.current };

    if (error || options?.length === 0) {
      return buildFailureOptions(
        TIME_OPTION_FLOW.JOB_START,
        t,
        nowISO,
        buildOpts
      );
    }

    if (!options) {
      return buildSkeletonOptions(
        TIME_OPTION_FLOW.JOB_START,
        t,
        nowISO,
        buildOpts
      );
    }

    return mapApiOptionsToUiOptions(
      options,
      TIME_OPTION_FLOW.JOB_START,
      t,
      nowISO
    );
  }, [error, options, t, nowISO]);

  const closeJobStartAtDialog = useCallback(() => {
    dispatch(actionCloseJobStartAtDialog());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    JobStartAtDialogEventEmitter.fireAndForget('cancel');
    closeJobStartAtDialog();
  }, [closeJobStartAtDialog]);

  const handleSelect = useCallback(
    (selectedValue) => {
      JobStartAtDialogEventEmitter.fireAndForget('success', selectedValue);
      closeJobStartAtDialog();
    },
    [closeJobStartAtDialog]
  );

  return (
    <BaseJobTimeDialog
      headerText={t('Select job start time')}
      optionsList={optionsList}
      tzid={tzid}
      onClose={handleClose}
      onSelect={handleSelect}
    />
  );
};

const JobStartAtDialog = () => {
  const isOpen = useSelector(getJobStartAtDialogOpen);

  if (!isOpen) return null;

  return <JobStartAtDialogBase />;
};

export default JobStartAtDialog;
